import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  IconButton,
  styled,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../../../theme";
import { EmailTemplateData } from "../types/email-templates";

interface EmailPreviewSidebarProps {
  templateData: EmailTemplateData;
}

const SkeletonBar = styled(Box)(() => ({
  borderRadius: 12,
  height: 12,
  marginBottom: 8,
}));

const buildPreviewHtml = (data: EmailTemplateData): string => {
  const heading = data.heading || data.template_name || "Email Heading";
  const message = (
    data.email_message || "Your email message will appear here."
  ).replace(/\n/g, "<br/>");
  const showButton = Boolean(data.button_label);

  return `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background:#F9FAFB; padding:24px;">
      <div style="max-width:480px; margin:0 auto; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 4px 6px -1px rgba(0,0,0,0.06);">
        ${
          data.banner_url
            ? `<img src="${data.banner_url}" style="width:100%; display:block;" />`
            : ""
        }
        <div style="padding:24px;">
          ${
            data.logo_url
              ? `<img src="${data.logo_url}" style="height:32px; margin-bottom:16px;" />`
              : ""
          }
          <h2 style="margin:0 0 12px; font-size:18px; color:#000;">${heading}</h2>
          <p style="margin:0 0 20px; font-size:14px; color:#334155; line-height:1.6;">${message}</p>
          ${
            showButton
              ? `<a href="${data.button_url || "#"}" style="display:inline-block; background:#614438; color:#fff; font-size:14px; font-weight:600; text-decoration:none; padding:10px 24px; border-radius:8px;">${data.button_label}</a>`
              : ""
          }
        </div>
        <div style="padding:16px 24px; background:#F8FAFC; border-top:1px solid #F1F5F9;">
          <p style="margin:0; font-size:11px; color:#94A3B8;">${data.footer_message || ""}</p>
        </div>
      </div>
    </div>
  `;
};

const EmailPreviewSidebar: React.FC<EmailPreviewSidebarProps> = ({
  templateData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDarkMode = theme.palette.mode === "dark";
  const [previewOpen, setPreviewOpen] = useState(false);

  const skeletonBg = isDarkMode ? `${colors.primary[400]}20` : "#F0F2F5";
  const textMain = isDarkMode ? "#ffffff" : "#1A202C";
  const textMuted = isDarkMode ? colors.primary[300] : "#718096";

  const hasSubject = Boolean(templateData.subject);
  const hasHeading = Boolean(templateData.heading);
  const hasMessage = Boolean(templateData.email_message);
  const hasMedia = Boolean(templateData.logo_url || templateData.banner_url);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {hasSubject ? (
        <Typography
          sx={{ fontWeight: 700, fontSize: "14px", color: textMain, mb: 1 }}
        >
          {templateData.subject}
        </Typography>
      ) : (
        <SkeletonBar sx={{ width: "85%", backgroundColor: skeletonBg }} />
      )}

      {hasHeading ? (
        <Typography sx={{ fontSize: "13px", color: textMuted, mb: 2 }}>
          {templateData.heading}
        </Typography>
      ) : (
        <SkeletonBar sx={{ width: "60%", backgroundColor: skeletonBg }} />
      )}

      {hasMessage ? (
        <Typography
          sx={{
            fontSize: "12px",
            color: textMuted,
            mb: 2,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {templateData.email_message}
        </Typography>
      ) : (
        <>
          <SkeletonBar sx={{ width: "95%", backgroundColor: skeletonBg }} />
          <SkeletonBar
            sx={{ width: "70%", backgroundColor: skeletonBg, mb: 2 }}
          />
        </>
      )}

      <Box
        sx={{
          borderTop: `1px solid ${isDarkMode ? colors.primary[700] : "#E2E8F0"}`,
          my: 2,
        }}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "14px",
            flexShrink: 0,
            backgroundColor: skeletonBg,
            backgroundImage: hasMedia
              ? `url(${templateData.logo_url || templateData.banner_url})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Box sx={{ flexGrow: 1, pt: 0.5 }}>
          <SkeletonBar sx={{ width: "80%", backgroundColor: skeletonBg }} />
          <SkeletonBar sx={{ width: "50%", backgroundColor: skeletonBg }} />
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Button
        fullWidth
        variant="outlined"
        onClick={() => setPreviewOpen(true)}
        sx={{
          textTransform: "none",
          borderRadius: "10px",
          color: colors.primary[700],
          borderColor: colors.primary[300] + "50",
          mt: 2,
        }}
      >
        Open Full Preview
      </Button>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #F1F5F9",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Email Preview</Typography>
          <IconButton size="small" onClick={() => setPreviewOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{ bgcolor: "#F9FAFB" }}
          dangerouslySetInnerHTML={{ __html: buildPreviewHtml(templateData) }}
        />
      </Dialog>
    </Box>
  );
};

export default EmailPreviewSidebar;
